import { Box, Divider, Link, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { numericSx } from '../../../theme/typography';
import { useTeamStatusScope } from '../hooks/useTeamStatusScope';
import { PanelCard } from './PanelCard';
import type { TeamStatusTodayCounts } from '../types';

interface TeamStatusTodayProps {
  counts: TeamStatusTodayCounts | undefined;
  loading?: boolean;
  /** Open the compliance report, where a reminder is actually composed. */
  onReviewCompliance: () => void;
}

type StatusTone = 'success' | 'warning' | 'info' | 'accent' | 'error';

interface StatusRow {
  key: keyof Omit<TeamStatusTodayCounts, 'totalActive'>;
  label: string;
  tone: StatusTone;
}

const ROWS: StatusRow[] = [
  { key: 'loggedToday', label: 'Logged today', tone: 'success' },
  // "Awaiting submission", never "Pending submission": the timesheets filter
  // uses "Awaiting approval" for the opposite state, and one word meaning both
  // "they owe it" and "you owe it" two clicks apart is how a reader chases the
  // wrong people.
  { key: 'pendingSubmission', label: 'Awaiting submission', tone: 'warning' },
  { key: 'onAnnualLeave', label: 'On annual leave', tone: 'info' },
  { key: 'onCallRota', label: 'On-call rota', tone: 'accent' },
  { key: 'missingEntries', label: 'Missing entries', tone: 'error' },
];

/**
 * "Team status today" — five counts over the population this reader may chase.
 *
 * ## The scope, and who sees this at all
 *
 * Both are the **server's** decision now, and deliberately the same rule
 * `POST /notifications/timesheet-reminders` enforces: an org-wide key
 * (`view:timesheets:all` / `manage:timesheets`) reaches everybody, anybody else
 * reaches their own report tree, and somebody with neither is served no widget.
 *
 * That replaced a role gate over org-wide data, which got it wrong in both
 * directions: a manager of six — the reader this widget is for — was shown
 * nothing, while an admin saw "80 missing" describing a population they had no
 * particular authority over. Sharing the rule with the nudge endpoint is what
 * stops the widget quoting a figure its reader would be refused if they acted
 * on it.
 *
 * The toggle appears only when the server says `canSeeEveryone`, and switching
 * re-reads this widget alone (see `useTeamStatusScope`).
 *
 * Data arrives from `widgets.teamStatusToday` on the `/api/dashboard/summary`
 * payload for the first paint.
 */
export function TeamStatusToday({
  counts: fromSummary,
  loading,
  onReviewCompliance,
}: TeamStatusTodayProps) {
  const { counts, scope, switching, error, setScope } = useTeamStatusScope(fromSummary);

  if (!counts && !loading) {
    return (
      <PanelCard title="Team status today" data-testid="widget-team-status-today">
        <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem', py: 2 }}>
          Status counts unavailable.
        </Typography>
      </PanelCard>
    );
  }

  const total = counts?.totalActive ?? 0;
  // The reminder audience, which is NOT `missingEntries` — see the footer.
  const outstanding = counts?.pendingSubmission ?? 0;

  return (
    <PanelCard
      title="Team status today"
      data-testid="widget-team-status-today"
      // A full-height control, so the header sizes to it rather than clipping —
      // see PanelCard's `anchorAction`.
      anchorAction={false}
      action={
        counts?.canSeeEveryone ? (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={scope}
            onChange={(_, next) => next && setScope(next)}
            disabled={switching}
            aria-label="Whose team status to show"
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', py: 0.25, px: 1, fontSize: '0.6875rem' } }}
          >
            <ToggleButton value="mine">My team</ToggleButton>
            <ToggleButton value="all">Everyone</ToggleButton>
          </ToggleButtonGroup>
        ) : undefined
      }
    >
      <Stack spacing="12px">
        {error && (
          <Typography sx={{ fontSize: '0.71875rem', color: 'error.main' }}>{error}</Typography>
        )}
        {ROWS.map((row) => {
          const value = counts?.[row.key] ?? 0;
          return (
            <Box
              key={row.key}
              sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}
            >
              <Box
                sx={(theme) => ({
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: toneColor(theme, row.tone),
                  flexShrink: 0,
                })}
              />
              <Typography
                sx={{
                  flex: 1,
                  fontSize: '0.78125rem',
                  color: 'text.primary',
                  lineHeight: 1.3,
                }}
              >
                {row.label}
              </Typography>
              <Typography
                sx={[
                  numericSx,
                  {
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: 'text.primary',
                  },
                ]}
              >
                {loading || switching ? '…' : value}
                <Box
                  component="span"
                  sx={{
                    color: 'text.disabled',
                    fontWeight: 400,
                    fontSize: '0.6875rem',
                    ml: '2px',
                  }}
                >
                  {' / '}
                  {total}
                </Box>
              </Typography>
            </Box>
          );
        })}

        {outstanding > 0 ? (
          <>
            <Divider sx={{ my: '4px' }} />
            {/*
              Links to the compliance report rather than opening a dialog here.

              Two reasons. The count that matters for a reminder is
              `pendingSubmission` — people whose period is missing, draft or
              rejected — NOT `missingEntries`, which is "has not logged an entry
              TODAY" and on a Monday morning is nearly everybody; chasing them
              mid-afternoon for a day that has not finished is the wrong action.
              And the compliance grid is where the decision is actually made:
              which week, who specifically, and when each of them was last
              chased. A dialog here would ask for that decision with none of the
              evidence on screen.
            */}
            <Link
              component="button"
              type="button"
              onClick={onReviewCompliance}
              sx={{
                alignSelf: 'flex-start',
                fontSize: '0.75rem',
                fontWeight: 600,
                textAlign: 'left',
              }}
            >
              Chase {outstanding} outstanding {outstanding === 1 ? 'timesheet' : 'timesheets'} →
            </Link>
          </>
        ) : null}
      </Stack>
    </PanelCard>
  );
}

function toneColor(
  theme: { palette: { success: { main: string }; warning: { main: string }; info: { main: string }; error: { main: string }; primary: { main: string } } },
  tone: StatusTone,
): string {
  switch (tone) {
    case 'success':
      return theme.palette.success.main;
    case 'warning':
      return theme.palette.warning.main;
    case 'info':
      return theme.palette.info.main;
    case 'accent':
      return theme.palette.primary.main;
    case 'error':
      return theme.palette.error.main;
  }
}
